// Role Management - Main component for admin role management
// Located in Admin Portal

import { Download, Loader2, Plus, Search, Upload } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../../hooks/use-toast';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { supabase } from '../../../utils/supabaseClient';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { RoleForm } from './RoleForm';
import { RoleList } from './RoleList';
import type { Role, SeedRole } from './types';

export const RoleManagement: React.FC = () => {
  const { roleData } = useUserPermissions();
  const tenantId = roleData?.organizationId || '';
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [jsonContent, setJsonContent] = useState<string>('');
  const [seededRoleCodes, setSeededRoleCodes] = useState<Set<string>>(new Set());
  const [seededRoleNames, setSeededRoleNames] = useState<Set<string>>(new Set());
  const [seedRoles, setSeedRoles] = useState<SeedRole[]>([]);
  const [isLoadingSeeds, setIsLoadingSeeds] = useState(false);
  const [selectedSeedRoleIds, setSelectedSeedRoleIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (tenantId) {
      loadRoles();
    }
    loadSeedRoles();
  }, [tenantId]);

  const loadRoles = async () => {
    if (!tenantId) return;

    setIsLoading(true);
    try {
      // Fetch both system roles (tenant_id IS NULL) and organization-specific roles
      const { data, error } = await supabase
        .from('base_roles')
        .select('*')
        .or(`tenant_id.is.null,tenant_id.eq.${tenantId}`)
        .eq('is_active', true)
        .order('role_type', { ascending: true }) // System roles first
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedRoles: Role[] = (data || []).map((item: any) => ({
        roleId: item.role_id,
        tenantId: item.tenant_id,
        roleName: item.role_name,
        roleCode: item.role_code,
        description: item.description,
        roleType: item.role_type,
        isActive: item.is_active,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
      }));

      setRoles(formattedRoles);
      
      // Track seeded roles (roles that already exist in DB)
      const codes = new Set(formattedRoles.map(r => r.roleCode));
      const names = new Set(formattedRoles.map(r => r.roleName));
      setSeededRoleCodes(codes);
      setSeededRoleNames(names);
    } catch (error) {
      console.error('Failed to load roles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setShowCreateForm(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setShowCreateForm(true);
  };

  const handleDeleteRole = async (roleId: string) => {
    // Find the role to check if it's a system role
    const roleToDelete = roles.find(r => r.roleId === roleId);

    if (roleToDelete?.roleType === 'system') {
      alert('System roles cannot be deleted.');
      return;
    }

    if (!confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('base_roles')
        .update({ is_active: false })
        .eq('role_id', roleId)
        .eq('tenant_id', tenantId);

      if (error) throw error;

      await loadRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
      alert('Failed to delete role. Please try again.');
    }
  };

  const handleFormClose = () => {
    setShowCreateForm(false);
    setEditingRole(null);
    loadRoles();
  };

  const generateRoleCode = (roleName: string): string => {
    return roleName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  const validateRoleData = (roleData: any): { valid: boolean; error?: string } => {
    if (!roleData.roleName) {
      return { valid: false, error: 'roleName is required' };
    }

    if (roleData.roleType && !['system', 'custom', 'auto', 'inherited'].includes(roleData.roleType)) {
      return { valid: false, error: `Invalid roleType: ${roleData.roleType}. Must be one of: system, custom, auto, inherited` };
    }

    return { valid: true };
  };

  const checkDuplicateRole = async (roleName: string, roleCode: string): Promise<boolean> => {
    if (!tenantId) return false;

    try {
      // Check for duplicate by role_name
      const { data: nameData, error: nameError } = await supabase
        .from('base_roles')
        .select('role_id')
        .eq('tenant_id', tenantId)
        .eq('role_name', roleName)
        .eq('is_active', true)
        .limit(1);

      if (nameError) throw nameError;
      if (nameData && nameData.length > 0) return true;

      // Check for duplicate by role_code
      const { data: codeData, error: codeError } = await supabase
        .from('base_roles')
        .select('role_id')
        .eq('tenant_id', tenantId)
        .eq('role_code', roleCode)
        .eq('is_active', true)
        .limit(1);

      if (codeError) throw codeError;
      return (codeData && codeData.length > 0);
    } catch (error) {
      console.error('Error checking duplicate role:', error);
      return false;
    }
  };

  const importRoleFromJson = async (roleData: any): Promise<{ success: boolean; error?: string }> => {
    if (!tenantId) {
      return { success: false, error: 'No organization selected' };
    }

    const validation = validateRoleData(roleData);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const roleName = roleData.roleName;
    const roleCode = roleData.roleCode || generateRoleCode(roleName);
    const description = roleData.description || null;
    const roleType = roleData.roleType || 'custom';

    const isDuplicate = await checkDuplicateRole(roleName, roleCode);
    if (isDuplicate) {
      return { success: false, error: `Role with name "${roleName}" or code "${roleCode}" already exists` };
    }

    try {
      // Insert role without roleAttributes and permissionCheck
      const { data, error } = await supabase
        .from('base_roles')
        .insert({
          tenant_id: tenantId,
          role_name: roleName.trim(),
          role_code: roleCode.trim(),
          description: description?.trim() || null,
          role_type: roleType,
          role_attributes: {},
          is_active: true,
        })
        .select('role_id')
        .single();

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Failed to import role:', error);
      return { success: false, error: error.message || 'Failed to import role' };
    }
  };

  const getTemplateRoleJson = (): string => {
    return JSON.stringify(
      [
        {
          roleName: 'Sales Manager',
          roleCode: 'sales_manager',
          description: 'Manages sales team and operations',
          roleType: 'custom',
        },
        {
          roleName: 'Support Agent',
          roleCode: 'support_agent',
          description: 'Handles customer support',
          roleType: 'custom',
        },
      ],
      null,
      2
    );
  };

  const handleToggleSeedRoleSelection = (seedRoleId: string) => {
    setSelectedSeedRoleIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(seedRoleId)) {
        newSet.delete(seedRoleId);
      } else {
        newSet.add(seedRoleId);
      }
      return newSet;
    });
  };

  const generateSeedRoleId = (seedRole: SeedRole): string => {
    return `seed-${seedRole.roleCode}-${seedRole.source || 'unknown'}`;
  };

  const handleLoadSeedRole = (seedRole: SeedRole) => {
    if (isSeedRoleSeeded(seedRole)) {
      toast({
        title: 'Role Already Seeded',
        description: `"${seedRole.roleName}" is already seeded and cannot be loaded`,
        variant: 'destructive',
      });
      return;
    }
    
    const seedJson = JSON.stringify(seedRole, null, 2);
    setJsonContent(seedJson);
    setSelectedSeedRoleIds(new Set([generateSeedRoleId(seedRole)]));
    toast({
      title: 'Seed Configuration Loaded',
      description: `Seed configuration for "${seedRole.roleName}" has been loaded into the textarea`,
    });
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder*="roleName"]');
      if (textarea) {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const handleLoadSelectedSeedRoles = () => {
    if (selectedSeedRoleIds.size === 0) {
      toast({
        title: 'No Roles Selected',
        description: 'Please select at least one seed role to load',
        variant: 'destructive',
      });
      return;
    }

    const selectedSeedRoles = seedRoles.filter(seedRole => 
      selectedSeedRoleIds.has(generateSeedRoleId(seedRole)) && !isSeedRoleSeeded(seedRole)
    );
    
    if (selectedSeedRoles.length === 0) {
      toast({
        title: 'No Valid Roles',
        description: 'All selected seed roles are already seeded',
        variant: 'destructive',
      });
      return;
    }
    
    const combinedJson = JSON.stringify(selectedSeedRoles.map(r => ({
      roleName: r.roleName,
      roleCode: r.roleCode,
      description: r.description,
      roleType: r.roleType,
    })), null, 2);
    setJsonContent(combinedJson);
    
    setTimeout(() => checkRolesFromJson(), 100);
    
    toast({
      title: 'Seed Roles Loaded',
      description: `Loaded ${selectedSeedRoles.length} seed role configuration(s)`,
    });
    
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder*="roleName"]');
      if (textarea) {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 200);
  };

  const loadSeedRoles = async () => {
    setIsLoadingSeeds(true);
    try {
      const seedFiles = [
        '/assets/role-seeds/basic-roles.json',
        '/assets/role-seeds/sales-roles.json',
        '/assets/role-seeds/support-roles.json',
        '/assets/role-seeds/engineering-roles.json',
      ];

      const allSeedRoles: SeedRole[] = [];

      for (const filePath of seedFiles) {
        try {
          const response = await fetch(filePath);
          if (response.ok) {
            const data = await response.json();
            const roles = Array.isArray(data) ? data : [data];
            const rolesWithSource = roles.map((role: any) => ({
              roleName: role.roleName,
              roleCode: role.roleCode,
              description: role.description,
              roleType: role.roleType || 'custom',
              source: filePath.split('/').pop()?.replace('.json', '') || 'unknown',
            }));
            allSeedRoles.push(...rolesWithSource);
          }
        } catch (error) {
          console.warn(`Failed to load seed file ${filePath}:`, error);
        }
      }

      setSeedRoles(allSeedRoles);
    } catch (error) {
      console.error('Failed to load seed roles:', error);
    } finally {
      setIsLoadingSeeds(false);
    }
  };

  const isSeedRoleSeeded = (seedRole: SeedRole): boolean => {
    return seededRoleCodes.has(seedRole.roleCode) || seededRoleNames.has(seedRole.roleName);
  };

  const checkRolesFromJson = useCallback(() => {
    if (!jsonContent.trim()) return;
    
    try {
      const parsed = JSON.parse(jsonContent);
      const rolesToCheck = Array.isArray(parsed) ? parsed : [parsed];
      
      // Check which roles from JSON already exist in the database
      // This will help identify which role cards should be disabled
      const existingCodes = new Set<string>();
      const existingNames = new Set<string>();
      
      rolesToCheck.forEach((roleData: any) => {
        if (roleData?.roleCode && seededRoleCodes.has(roleData.roleCode)) {
          existingCodes.add(roleData.roleCode);
        }
        if (roleData?.roleName && seededRoleNames.has(roleData.roleName)) {
          existingNames.add(roleData.roleName);
        }
      });
      
      // The seeded sets are already populated from loadRoles
      // This function just ensures we're aware of which roles in JSON are seeded
    } catch (error) {
      // Invalid JSON, ignore
    }
  }, [jsonContent, seededRoleCodes, seededRoleNames]);

  // Check for seeded roles when JSON content changes
  useEffect(() => {
    if (jsonContent.trim() && seededRoleCodes.size > 0) {
      const timeoutId = setTimeout(() => {
        checkRolesFromJson();
      }, 500); // Debounce check
      return () => clearTimeout(timeoutId);
    }
  }, [jsonContent, checkRolesFromJson, seededRoleCodes]);

  const handleDownloadTemplate = () => {
    const template = getTemplateRoleJson();
    const blob = new Blob([template], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'role_template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: 'Template Downloaded',
      description: 'Role template JSON has been downloaded',
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please select a JSON file',
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        JSON.parse(content); // Validate JSON
        setJsonContent(content);
        // Check for seeded roles after loading
        setTimeout(() => checkRolesFromJson(), 100);
        toast({
          title: 'File Loaded',
          description: 'JSON file has been loaded into the textarea',
        });
      } catch (error) {
        toast({
          title: 'Invalid JSON',
          description: 'The uploaded file contains invalid JSON',
          variant: 'destructive',
        });
      }
    };

    reader.onerror = () => {
      toast({
        title: 'File Read Error',
        description: 'Failed to read the selected file',
        variant: 'destructive',
      });
    };

    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportFromTextarea = async () => {
    if (!jsonContent.trim()) {
      toast({
        title: 'Empty JSON',
        description: 'Please provide JSON content to import',
        variant: 'destructive',
      });
      return;
    }

    if (!tenantId) {
      toast({
        title: 'No Organization',
        description: 'Please ensure you have an organization selected',
        variant: 'destructive',
      });
      return;
    }

    setIsImporting(true);

    try {
      const parsedData = JSON.parse(jsonContent);

      let rolesToImport: any[] = [];
      if (Array.isArray(parsedData)) {
        rolesToImport = parsedData;
      } else if (typeof parsedData === 'object' && parsedData !== null) {
        rolesToImport = [parsedData];
      } else {
        throw new Error('Invalid JSON structure. Expected an object or array of role objects.');
      }

      if (rolesToImport.length === 0) {
        throw new Error('No roles found in JSON');
      }

      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (let i = 0; i < rolesToImport.length; i++) {
        const roleData = rolesToImport[i];
        const result = await importRoleFromJson(roleData);

        if (result.success) {
          successCount++;
        } else {
          errorCount++;
          errors.push(`Role ${i + 1}: ${result.error || 'Unknown error'}`);
        }
      }

      if (errorCount === 0) {
        toast({
          title: 'Import Successful',
          description: `Successfully imported ${successCount} role(s)`,
        });
        setJsonContent('');
        setSelectedSeedRoleIds(new Set());
        await loadRoles(); // This will update seededRoleCodes and seededRoleNames
      } else if (successCount > 0) {
        toast({
          title: 'Partial Import Success',
          description: `Imported ${successCount} role(s), ${errorCount} failed. ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`,
          variant: 'destructive',
        });
        await loadRoles();
      } else {
        toast({
          title: 'Import Failed',
          description: `Failed to import all roles. ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('JSON import error:', error);
      toast({
        title: 'Import Error',
        description: error.message || 'Failed to parse or import JSON',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const filteredRoles = roles.filter((role) =>
    role.roleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.roleCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (showCreateForm) {
    return (
      <RoleForm
        role={editingRole}
        tenantId={tenantId}
        onClose={handleFormClose}
        onSave={handleFormClose}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Role Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage roles with granular permissions
          </p>
        </div>
        <Button onClick={handleCreateRole}>
          <Plus className="h-4 w-4 mr-2" />
          Create Role
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Roles</CardTitle>
              <CardDescription>
                Manage roles and their associated policies
              </CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading roles...</div>
            </div>
          ) : (
            <RoleList
              roles={filteredRoles}
              onEdit={handleEditRole}
              onDelete={handleDeleteRole}
            />
          )}
        </CardContent>
      </Card>

      
      {/* Sample JSON Section */}
      <Card className="mt-8 mb-16">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Sample JSON</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
              >
                <Download className="h-4 w-4 mr-2" />
                Template
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleUploadClick}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload File
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Seed Roles from JSON Files */}
          {seedRoles.length > 0 && (
            <div className="mb-4 border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium">
                  Seed Roles from Assets
                  {selectedSeedRoleIds.size > 0 && (
                    <span className="ml-2 text-xs text-muted-foreground font-normal">
                      ({selectedSeedRoleIds.size} selected)
                    </span>
                  )}
                </h3>
                {selectedSeedRoleIds.size > 0 && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadSelectedSeedRoles}
                    >
                      <Upload className="h-3 w-3 mr-2" />
                      Load Selected ({selectedSeedRoleIds.size})
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedSeedRoleIds(new Set());
                        toast({
                          title: 'Selection Cleared',
                          description: 'All seed role selections have been cleared',
                        });
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
              {isLoadingSeeds ? (
                <div className="flex items-center justify-center py-4">
                  <div className="text-sm text-muted-foreground">Loading seed roles...</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {seedRoles.map((seedRole) => {
                    const isSeeded = isSeedRoleSeeded(seedRole);
                    const seedRoleId = generateSeedRoleId(seedRole);
                    const isSelected = selectedSeedRoleIds.has(seedRoleId);
                    
                    return (
                      <Card
                        key={seedRoleId}
                        className={`transition-colors ${
                          isSeeded
                            ? 'opacity-50 cursor-not-allowed bg-muted'
                            : isSelected
                            ? 'bg-accent border-primary cursor-pointer'
                            : 'hover:bg-accent cursor-pointer'
                        }`}
                        onClick={(e) => {
                          if (isSeeded) return;
                          if ((e.target as HTMLElement).closest('input[type="checkbox"]')) {
                            return;
                          }
                          handleToggleSeedRoleSelection(seedRoleId);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isSeeded}
                              onChange={() => !isSeeded && handleToggleSeedRoleSelection(seedRoleId)}
                              onClick={(e) => e.stopPropagation()}
                              className={`mt-1 h-4 w-4 ${
                                isSeeded ? 'cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            />
                            <div className="flex items-start justify-between gap-2 flex-1 min-w-0">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-semibold text-sm truncate">{seedRole.roleName}</h4>
                                  {isSeeded && (
                                    <Badge variant="secondary" className="text-xs flex-shrink-0">
                                      Seeded
                                    </Badge>
                                  )}
                                  {seedRole.source && (
                                    <Badge variant="outline" className="text-xs flex-shrink-0">
                                      {seedRole.source}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 truncate">
                                  {seedRole.roleCode}
                                </p>
                                {seedRole.description && (
                                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                                    {seedRole.description}
                                  </p>
                                )}
                              </div>
                              <Badge variant="outline" className="ml-2 flex-shrink-0">
                                {seedRole.roleType}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <Textarea
            value={jsonContent}
            onChange={(e) => {
              setJsonContent(e.target.value);
              // Clear selection when user manually edits
              if (selectedSeedRoleIds.size > 0) {
                setSelectedSeedRoleIds(new Set());
              }
            }}
            placeholder='{"roleName": "Sales Manager", "roleCode": "sales_manager", "description": "Manages sales team", "roleType": "custom"}'
            className="font-mono text-sm min-h-[200px]"
            rows={10}
          />
          <div className="flex gap-2">
            <Button
              onClick={handleImportFromTextarea}
              disabled={isImporting || !tenantId || !jsonContent.trim()}
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import JSON
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

